import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tag } from 'primereact/tag';
import axios from 'axios';
import { url } from '../../../../../env';
import {
    LEGAL_SCHEMA, getLegalFields, categoryForType,
    normalizeExtracted as normalizeExtractedShared,
    deserializeFieldValue, serializeFieldValue,
} from '../shared/legalEditorSchema';
import StructuredLegalEditor from '../shared/StructuredLegalEditor';
import OperationalReviewModal from '../shared/OperationalReviewModal';
import { AppTextField, AppButton, AppIconButton } from '../../../../../Components/FormKit';
import { AppTableCard } from '../../../../../Components/TableKit';

// ─── Constantes de color ─────────────────────────────────────────────────────
const PURPLE = '#7B1FA2';
const PURPLE_LIGHT = '#F3E8FF';

// ─── Helpers visuales ────────────────────────────────────────────────────────
const NA = () => <span className="text-500 font-italic">N/A</span>;

const FieldVal = ({ value }) => {
    if (value === null || value === undefined || value === '' || value === 'null') return <NA />;
    if (typeof value === 'object') return <span className="text-500 font-italic">[objeto]</span>;
    return <span className="text-900">{String(value)}</span>;
};

const LabelValue = ({ label, value }) => (
    <div className="mb-2">
        <div className="text-xs text-500 font-semibold mb-1" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {label}
        </div>
        <div className="text-sm text-900 font-medium">
            <FieldVal value={value} />
        </div>
    </div>
);

const TextBlock = ({ label, value }) => (
    <div className="mb-2">
        <div className="text-xs text-500 font-semibold mb-1" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {label}
        </div>
        {value ? (
            <div className="text-sm text-700 border-round p-2" style={{ lineHeight: 1.6, background: '#F5F8FF', borderLeft: '3px solid #1565C0' }}>
                {value}
            </div>
        ) : <NA />}
    </div>
);

const InlineTable = ({ data, columns, emptyText = 'Sin registros' }) => {
    if (!data || !Array.isArray(data) || !data.length) {
        return <div className="text-xs text-500 font-italic py-1">{emptyText}</div>;
    }
    const cols = columns || Object.keys(data[0]).filter(k => {
        const v = data[0][k];
        return typeof v !== 'object' || v === null;
    });
    if (!cols.length) return null;
    return (
        <AppTableCard animated={false}>
            <table className="tk-table tk-table--compact tk-table--striped">
                <thead>
                    <tr>
                        {cols.map(col => (
                            <th key={col}>
                                {col.replace(/_/g, ' ')}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, i) => (
                        <tr key={i}>
                            {cols.map(col => (
                                <td key={col} className="text-700" style={{ verticalAlign: 'top' }}>
                                    {row[col] !== null && row[col] !== undefined && row[col] !== ''
                                        ? String(row[col])
                                        : <span className="text-400">—</span>}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </AppTableCard>
    );
};

const InlineList = ({ data, emptyText = 'Sin registros' }) => {
    if (!data || !Array.isArray(data) || !data.length) {
        return <div className="text-xs text-500 font-italic">{emptyText}</div>;
    }
    return (
        <ul className="m-0 text-sm text-700" style={{ paddingLeft: '1.25rem' }}>
            {data.map((item, i) => (
                <li key={i} className="mb-1">
                    {typeof item === 'string' ? item : (item.nombre || item.name || JSON.stringify(item))}
                </li>
            ))}
        </ul>
    );
};

// ─── Normalización legacy (importada de shared/legalEditorSchema) ─────────────
const normalizeExtracted = normalizeExtractedShared;

const ensureArray = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v;
    return [v];
};

// ─── Vigencia y firmantes ────────────────────────────────────────────────────
const isDocCompleted = (status) => status === 'completed' || status === 'ok' || status === 'warning';

const isValidLegalDoc = (doc) => {
    if (doc.status === 'error' || doc.status === 'failed') return false;
    if (doc.detected_type === 'DESCONOCIDO' || !doc.detected_type) return false;
    if (doc.extracted?.tipo_detectado === 'DESCONOCIDO') return false;
    return true;
};

const computeVigencia = (docs) => {
    const active = docs.filter(d => d.review_status !== 'rejected' && isDocCompleted(d.status) && isValidLegalDoc(d));
    if (!active.length) return { vigentes: [], empatadas: false, maxDate: null };
    const withDate = active.filter(d => d.vigencia_date);
    if (!withDate.length) return { vigentes: active, empatadas: active.length > 1, maxDate: null };
    const maxDate = withDate.reduce((m, d) => d.vigencia_date > m ? d.vigencia_date : m, withDate[0].vigencia_date);
    const vigentes = withDate.filter(d => d.vigencia_date === maxDate);
    return { vigentes, empatadas: vigentes.length > 1, maxDate };
};

const getSignatories = (vigenteDocs, allCompleted, maxDate) => {
    const result = [];
    const alerts = [];
    const seen = new Set();
    const vigenteIds = new Set(vigenteDocs.map(d => d.id));
    const sources = vigenteDocs.length ? vigenteDocs : allCompleted;
    // Always include ACTA_CONSTI from allCompleted (foundational doc for firmantes)
    const actasNotInVigentes = allCompleted.filter(d => d.detected_type === 'ACTA_CONSTI' && !vigenteIds.has(d.id));
    const combined = [...sources, ...actasNotInVigentes];

    for (const doc of combined) {
        const ext = normalizeExtracted(doc.extracted);
        const type = doc.detected_type;
        const fuente = { ACTA_CONSTI: 'ACTA CONSTI', PODERES: 'PODERES', REGISTRO_PUBLICO: 'REGISTRO' }[type] || type;

        if (type === 'ACTA_CONSTI') {
            const isNoVigente = !vigenteIds.has(doc.id);
            if (isNoVigente) {
                const reason = !doc.vigencia_date
                    ? `"${doc.file_name}" no tiene fecha de constitución registrada`
                    : `"${doc.file_name}" tiene fecha ${doc.vigencia_date} pero existe un documento más reciente (${maxDate})`;
                alerts.push(reason);
            }
            ensureArray(ext.personas_con_poder_firma).forEach(p => {
                const nombre = p.nombre || p.name || (typeof p === 'string' ? p : null);
                if (nombre && !seen.has(nombre)) {
                    seen.add(nombre);
                    result.push({
                        nombre,
                        cargo: p.cargo || p.tipo || p.facultades || 'Firmante',
                        fuente,
                        estado: isNoVigente ? 'Acta no vigente' : 'Vigente',
                    });
                }
            });
        }
        if (type === 'PODERES') {
            ensureArray(ext.apoderados).forEach(p => {
                const nombre = p.nombre || p.name || (typeof p === 'string' ? p : null);
                if (nombre && !seen.has(nombre)) {
                    seen.add(nombre);
                    result.push({ nombre, cargo: p.dominio || p.cargo || p.facultades || 'Apoderado', fuente, estado: 'Vigente' });
                }
            });
        }
        if (type === 'REGISTRO_PUBLICO') {
            ensureArray(ext.administradores_inscritos_con_poder).forEach(p => {
                const nombre = typeof p === 'string' ? p : (p.nombre || p.name || null);
                if (nombre && !seen.has(nombre)) {
                    seen.add(nombre);
                    result.push({ nombre, cargo: p.cargo || 'Administrador', fuente, estado: 'Vigente' });
                }
            });
        }
    }
    return { signatories: result, alerts };
};

// ─── Cuadro Accionario helpers ───────────────────────────────────────────────
const normalizeShareholder = (row) => ({
    nombre: row.nombre || row.accionista || row.name || 'Desconocido',
    acciones: row.acciones ?? row.num_acciones ?? null,
    valor: row.valor ?? row.valor_acciones ?? null,
    porcentaje: row.porcentaje ?? row.participacion ?? null,
});

const getCuadroData = (vigenteDocs, allCompleted) => {
    const sources = vigenteDocs.length ? vigenteDocs : allCompleted;
    const results = [];
    for (const doc of sources) {
        const ext = normalizeExtracted(doc.extracted);
        const raw = ext.cuadro_accionario || ext.cuadro_accionario_original;
        const arr = ensureArray(raw);
        if (arr.length) {
            results.push({
                docId: doc.id,
                fileName: doc.file_name,
                detectedType: doc.detected_type,
                shareholders: arr.map(normalizeShareholder),
            });
        }
    }
    return results;
};

const areCuadrosEqual = (sources) => {
    if (sources.length <= 1) return true;
    const key = (s) => s.shareholders.map(sh => sh.nombre.toUpperCase().trim()).sort().join('|');
    const first = key(sources[0]);
    return sources.every(s => key(s) === first);
};

// ─── Metadatos por tipo ──────────────────────────────────────────────────────
const TYPE_META = {
    ACTA_CONSTI: { icon: 'pi-building', label: 'Acta Constitutiva', color: PURPLE },
    PODERES: { icon: 'pi-id-card', label: 'Poderes Notariales', color: '#1565C0' },
    REGISTRO_PUBLICO: { icon: 'pi-verified', label: 'Registro Público', color: '#2E7D32' },
    DESCONOCIDO: { icon: 'pi-question-circle', label: 'Tipo Desconocido', color: '#9ca3af' },
};
const getTypeMeta = (type) => TYPE_META[type] || TYPE_META.DESCONOCIDO;

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const Skel = ({ h = '1rem', w = '100%' }) => (
    <div style={{ height: h, width: w, background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', borderRadius: '4px', animation: 'legalSkel 1.4s infinite' }} />
);

const LegalSkeleton = () => (
    <div className="flex flex-column gap-3">
        <style>{`@keyframes legalSkel{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        <div className="flex gap-3 flex-wrap">
            {[1, 2].map(i => (
                <div key={i} className="bg-white border-1 surface-border border-round-lg p-3" style={{ flex: i === 1 ? '1 1 240px' : '2 1 360px' }}>
                    <Skel h="1rem" w="40%" /><div className="mb-2" />
                    <Skel h="0.85rem" /><div className="mb-1" />
                    <Skel h="0.85rem" w="70%" />
                </div>
            ))}
        </div>
        {[1, 2].map(i => (
            <div key={i} className="bg-white border-1 surface-border border-round-lg p-3">
                <Skel h="1rem" w="50%" /><div className="mb-2" />
                <Skel h="0.85rem" /><div className="mb-1" />
                <Skel h="0.85rem" w="80%" />
            </div>
        ))}
    </div>
);

// ─── Bento: Vigencia ──────────────────────────────────────────────────────────
const VigenciaCard = ({ vigentes, empatadas, allDocs, maxDate }) => {
    const activeDocs = allDocs.filter(d => d.review_status !== 'rejected' && isValidLegalDoc(d));
    const estadoGeneral = !activeDocs.length
        ? 'Sin vigencia'
        : activeDocs.some(d => d.status === 'processing' || d.status === 'pending')
            ? 'En revisión'
            : vigentes.length ? 'Vigente' : 'Sin vigencia';
    const estadoSeverity = { 'Vigente': 'success', 'En revisión': 'warning', 'Sin vigencia': 'danger' }[estadoGeneral];

    return (
        <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1" style={{ flex: '1 1 300px', minWidth: '240px', borderTop: `3px solid ${PURPLE}` }}>
            <div className="flex align-items-center gap-2 mb-3">
                <i className="pi pi-calendar text-lg" style={{ color: PURPLE }} />
                <span className="text-sm font-bold text-900" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Vigencia Legal
                </span>
            </div>
            <div className="flex flex-column gap-2">
                <div className="flex justify-content-between align-items-center">
                    <span className="text-xs text-500" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>Última actualización</span>
                    <span className="text-sm font-semibold text-900">
                        {maxDate || <span className="text-500">N/A</span>}
                    </span>
                </div>
                <div className="flex justify-content-between align-items-center">
                    <span className="text-xs text-500" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>Documentos activos</span>
                    <span className="text-xl font-bold" style={{ color: PURPLE }}>{activeDocs.length}</span>
                </div>
                <div className="flex justify-content-between align-items-center">
                    <span className="text-xs text-500" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>Estado general</span>
                    <Tag value={estadoGeneral} severity={estadoSeverity} rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />
                </div>
                {empatadas && (
                    <div className="flex align-items-center gap-2 mt-1 p-2 border-round text-xs" style={{ background: '#FFF8E1', border: '1px solid #FFC107', color: '#B45309' }}>
                        <i className="pi pi-info-circle text-xs" />
                        Misma vigencia detectada en múltiples documentos
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Bento: Firmantes ─────────────────────────────────────────────────────────
const FuenteBadge = ({ fuente }) => {
    const colors = {
        'ACTA CONSTI': { bg: PURPLE_LIGHT, text: PURPLE },
        'PODERES': { bg: '#EFF6FF', text: '#1D4ED8' },
        'REGISTRO': { bg: '#ECFDF5', text: '#065F46' },
    };
    const c = colors[fuente] || { bg: '#f3f4f6', text: '#6b7280' };
    return (
        <span className="font-semibold white-space-nowrap" style={{ fontSize: '0.7rem', background: c.bg, color: c.text, padding: '0.15rem 0.45rem', borderRadius: '10px' }}>
            {fuente}
        </span>
    );
};

const SignatoriesCard = ({ signatories, alerts = [] }) => (
    <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1" style={{ flex: '1 1 300px', minWidth: '240px', borderTop: `3px solid ${PURPLE}` }}>
        <div className="flex align-items-center gap-2 mb-3">
            <i className="pi pi-users text-lg" style={{ color: PURPLE }} />
            <span className="text-sm font-bold text-900" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Personas con poder para firmar
            </span>
        </div>
        {alerts.length > 0 && (
            <div className="mb-3 flex flex-column gap-1">
                {alerts.map((msg, i) => (
                    <div key={i} className="flex align-items-start gap-2 text-xs border-round p-2" style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412' }}>
                        <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.8rem', marginTop: '1px', color: '#ea580c' }} />
                        <span>{msg}</span>
                    </div>
                ))}
            </div>
        )}
        {signatories.length === 0 ? (
            <div className="text-sm text-500 font-italic py-1">
                No se detectaron firmantes en los documentos vigentes.
            </div>
        ) : (
            <AppTableCard animated={false}>
                <table className="tk-table tk-table--compact">
                    <thead>
                        <tr>
                            {['Nombre Completo', 'Cargo / Tipo', 'Fuente de Datos', 'Estado'].map(h => (
                                <th key={h}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {signatories.map((s, i) => (
                            <tr key={i}>
                                <td className="font-medium text-900">{s.nombre}</td>
                                <td className="text-700">{s.cargo}</td>
                                <td><FuenteBadge fuente={s.fuente} /></td>
                                <td>
                                    <Tag value={s.estado} severity={s.estado === 'Vigente' ? 'success' : 'warning'} rounded style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem' }} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </AppTableCard>
        )}
    </div>
);

// ─── Bento: Cuadro Accionario ────────────────────────────────────────────────
const ShareholderTable = ({ shareholders, columns }) => {
    const colLabels = { nombre: 'Accionista', acciones: 'Acciones', valor: 'Valor', porcentaje: '%' };
    return (
        <AppTableCard animated={false}>
            <table className="tk-table tk-table--compact tk-table--striped">
                <thead>
                    <tr>
                        {columns.map(col => (
                            <th key={col}>
                                {colLabels[col] || col}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {shareholders.map((sh, i) => (
                        <tr key={i}>
                            {columns.map(col => (
                                <td key={col} className={col === 'nombre' ? 'font-medium text-900' : 'text-700'}>
                                    {sh[col] !== null && sh[col] !== undefined ? String(sh[col]) : <span className="text-400">—</span>}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </AppTableCard>
    );
};

const CuadroAccionarioCard = ({ cuadroSources }) => {
    if (!cuadroSources.length) return null;
    const same = areCuadrosEqual(cuadroSources);
    const displayCols = ['nombre', 'acciones', 'valor', 'porcentaje'];
    const activeCols = displayCols.filter(c =>
        cuadroSources.some(s => s.shareholders.some(sh => sh[c] !== null && sh[c] !== undefined))
    );

    return (
        <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1" style={{ flex: '1 1 300px', minWidth: '240px', borderTop: `3px solid ${PURPLE}` }}>
            <div className="flex align-items-center gap-2 mb-3">
                <i className="pi pi-chart-pie text-lg" style={{ color: PURPLE }} />
                <span className="text-sm font-bold text-900" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Cuadro Accionario
                </span>
            </div>
            {same ? (
                <ShareholderTable shareholders={cuadroSources[0].shareholders} columns={activeCols} />
            ) : (
                <div className="flex flex-column gap-3">
                    {cuadroSources.map((source, i) => (
                        <div key={source.docId}>
                            <div className="text-xs text-500 font-semibold mb-2">
                                Fuente: {source.fileName || `Documento ${i + 1}`}
                            </div>
                            <ShareholderTable shareholders={source.shareholders} columns={activeCols} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Campos por tipo de documento ─────────────────────────────────────────────
const KNOWN_FIELDS = {
    ACTA_CONSTI: ['razon_social', 'fecha_constitucion', 'duracion_sociedad', 'domicilio_social', 'notario_y_escritura', 'capital_social_minimo', 'capital_variable', 'tipo_administracion', 'objeto_social_resumen', 'cuadro_accionario', 'cuadro_accionario_original', 'personas_con_poder_firma', 'comisarios'],
    PODERES: ['fecha_documento', 'fecha_poder', 'razon_social', 'notario_y_escritura', 'otorgantes', 'apoderados', 'apoderado', 'limites_del_poder'],
    REGISTRO_PUBLICO: ['razon_social', 'folio_mercantil_fme', 'fecha_inscripcion_original', 'fecha_inscripcion', 'reformas_estatutos', 'administradores_inscritos_con_poder', 'ultimo_administrador_inscrito', 'cuadro_accionario_inscrito'],
};

const SectionLabel = ({ children }) => (
    <div className="text-xs text-500 font-semibold mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {children}
    </div>
);

const ActaFields = ({ ext }) => (
    <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem 1.5rem' }}>
            <LabelValue label="Razón Social" value={ext.razon_social} />
            <LabelValue label="Fecha de Constitución" value={ext.fecha_constitucion} />
            <LabelValue label="Duración" value={ext.duracion_sociedad} />
            <LabelValue label="Domicilio Social" value={ext.domicilio_social} />
            <LabelValue label="Notario y Escritura" value={ext.notario_y_escritura} />
            <LabelValue label="Capital Social Mínimo" value={ext.capital_social_minimo} />
            <LabelValue label="Capital Variable" value={ext.capital_variable} />
            <LabelValue label="Tipo de Administración" value={ext.tipo_administracion} />
        </div>
        {ext.objeto_social_resumen && (
            <div className="mt-3">
                <TextBlock label="Objeto Social" value={ext.objeto_social_resumen} />
            </div>
        )}
        {ext.cuadro_accionario && (
            <div className="mt-3">
                <SectionLabel>Cuadro Accionario</SectionLabel>
                <InlineTable data={ensureArray(ext.cuadro_accionario)} />
            </div>
        )}
        {ext.personas_con_poder_firma && (
            <div className="mt-3">
                <SectionLabel>Personas con Poder para Firmar</SectionLabel>
                <InlineTable data={ensureArray(ext.personas_con_poder_firma)} />
            </div>
        )}
        {ext.comisarios && (
            <div className="mt-3">
                <SectionLabel>Comisarios</SectionLabel>
                <InlineList data={ensureArray(ext.comisarios)} />
            </div>
        )}
    </>
);

const PoderesFields = ({ ext }) => {
    const apoderados = ensureArray(ext.apoderados);
    const possibleCols = ['nombre', 'dominio', 'administracion', 'pleitos_cobranzas', 'titulos_arrendamiento', 'limites_o_mancomunado'];
    const activeCols = possibleCols.filter(c => apoderados.some(a => a[c] !== undefined && a[c] !== null && a[c] !== ''));
    return (
        <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem 1.5rem' }}>
                <LabelValue label="Fecha del Documento" value={ext.fecha_documento} />
                <LabelValue label="Razón Social" value={ext.razon_social} />
                <LabelValue label="Notario y Escritura" value={ext.notario_y_escritura} />
                <LabelValue label="Otorgantes" value={Array.isArray(ext.otorgantes) ? ext.otorgantes.join(', ') : ext.otorgantes} />
            </div>
            {apoderados.length > 0 && (
                <div className="mt-3">
                    <SectionLabel>Apoderados</SectionLabel>
                    <InlineTable data={apoderados} columns={activeCols.length ? activeCols : undefined} />
                </div>
            )}
        </>
    );
};

const RegistroFields = ({ ext }) => (
    <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem 1.5rem' }}>
            <LabelValue label="Razón Social" value={ext.razon_social} />
            <LabelValue label="Folio Mercantil (FME)" value={ext.folio_mercantil_fme} />
            <LabelValue label="Fecha de Inscripción" value={ext.fecha_inscripcion_original} />
        </div>
        {ext.reformas_estatutos && (
            <div className="mt-3">
                <TextBlock label="Reformas de Estatutos" value={typeof ext.reformas_estatutos === 'string' ? ext.reformas_estatutos : JSON.stringify(ext.reformas_estatutos)} />
            </div>
        )}
        {ext.administradores_inscritos_con_poder && (
            <div className="mt-3">
                <SectionLabel>Administradores Inscritos con Poder</SectionLabel>
                <InlineList data={ensureArray(ext.administradores_inscritos_con_poder)} />
            </div>
        )}
        {ext.cuadro_accionario_inscrito && (
            <div className="mt-3">
                <SectionLabel>Cuadro Accionario Inscrito</SectionLabel>
                <InlineTable data={ensureArray(ext.cuadro_accionario_inscrito)} />
            </div>
        )}
    </>
);

const ExtraFields = ({ ext, type }) => {
    const known = KNOWN_FIELDS[type] || [];
    const extras = Object.entries(ext).filter(([k, v]) => !known.includes(k) && v !== null && v !== undefined && v !== '');
    if (!extras.length) return null;
    return (
        <div className="mt-3 surface-ground border-1 surface-border border-round p-3">
            <div className="text-xs text-400 font-semibold mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Otros campos extraídos
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.5rem 1rem' }}>
                {extras.map(([k, v]) => (
                    <div key={k}>
                        <div className="text-xs text-400 font-semibold" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k.replace(/_/g, ' ')}</div>
                        <div className="text-sm text-700">
                            {typeof v === 'object'
                                ? (Array.isArray(v) ? `[${v.length} registros]` : '[objeto]')
                                : String(v)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── LegalEditForm (uses shared StructuredLegalEditor) — now renders as pure content inside modal ──
const LegalEditForm = ({ doc, activeOverridePaths, editValues, saveReason, onFieldChange, onReasonChange, onClearPath, onSave, onCancel, saving }) => {
    const schema = LEGAL_SCHEMA[doc.detected_type];
    const meta = getTypeMeta(doc.detected_type);
    if (!schema) return null;

    return (
        <div className="fk-root">
            {doc.has_type_mismatch && (
                <div className="mb-3 p-2 border-round text-xs"
                    style={{ background: '#FFF7ED', border: '1px solid #FED7AA', color: '#9A3412' }}>
                    <i className="pi pi-info-circle mr-1" />
                    Este documento fue subido como <strong>{doc.source_category}</strong> pero la IA lo
                    detectó como <strong>{schema.label}</strong>. Se editan los campos del tipo detectado.
                </div>
            )}

            <StructuredLegalEditor
                schema={schema}
                editValues={editValues}
                activeOverridePaths={activeOverridePaths}
                accentColor={meta.color}
                onFieldChange={onFieldChange}
                onClearPath={onClearPath}
            />

            <div className="mb-3 mt-2">
                <AppTextField
                    label="Motivo (opcional)"
                    value={saveReason}
                    onChange={e => onReasonChange(e.target.value)}
                    placeholder="Ej: Corrección manual desde documento legal"
                />
            </div>

            <div className="flex justify-content-end gap-2">
                <AppButton label="Cancelar" variant="secondary" onClick={onCancel} disabled={saving} />
                <AppButton label="Guardar datos confirmados" onClick={onSave}
                    disabled={saving} loading={saving} />
            </div>
        </div>
    );
};

// ─── Card de expediente ───────────────────────────────────────────────────────
const DocCard = ({ doc, isVigente, isEmpatado, onEditClick, editContent }) => {
    const [open, setOpen] = useState(true);
    const meta = getTypeMeta(doc.detected_type);
    const ext = normalizeExtracted(doc.extracted);
    const isAnalyzing = doc.status === 'processing' || doc.status === 'pending';
    const isFailed = doc.status === 'failed' || doc.status === 'error';
    const isDesconocido = doc.detected_type === 'DESCONOCIDO' || !doc.detected_type || doc.extracted?.tipo_detectado === 'DESCONOCIDO';

    return (
        <div className="bg-white border-round-lg overflow-hidden shadow-1" style={{ border: `1px solid ${isDesconocido ? '#fbbf24' : 'var(--surface-border)'}`, borderLeft: `4px solid ${meta.color}` }}>
            {/* Header */}
            <div
                className="flex align-items-center gap-3 cursor-pointer"
                style={{ padding: '0.75rem 1rem', background: `${meta.color}08`, borderBottom: open ? '1px solid #f3f4f6' : 'none' }}
                onClick={() => setOpen(o => !o)}
            >
                <i className={`pi ${meta.icon}`} style={{ fontSize: '1.1rem', color: meta.color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="text-sm font-semibold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {doc.file_name || 'Documento Legal'}
                    </div>
                    <div className="text-xs text-500 mt-1">
                        {meta.label}
                        {doc.vigencia_date && <span className="ml-2">• {doc.vigencia_date}</span>}
                    </div>
                </div>
                <div className="flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                    {isAnalyzing && <Tag value="Analizando" severity="warning" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />}
                    {isFailed && <Tag value="Error" severity="danger" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />}
                    {isVigente && !isAnalyzing && (
                        <Tag value={isEmpatado ? 'Vigente *' : 'Vigente'} severity="success" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />
                    )}
                    {isDesconocido && !isAnalyzing && (
                        <Tag value="Tipo desconocido" severity="warning" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />
                    )}
                    {doc.has_type_mismatch && !isAnalyzing && !isDesconocido && (
                        <Tag value="Tipo reclasificado" severity="info" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />
                    )}
                    {doc.has_manual_overrides && (
                        <Tag value="Corregido" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', background: `${meta.color}20`, color: meta.color }} />
                    )}
                    {onEditClick && !editContent && (
                        <AppButton label="Editar" icon="pi pi-pencil" variant="info" size="sm" className="Button p-0"
                            onClick={e => { e.stopPropagation(); onEditClick(); }} />
                    )}
                    <i className={`pi ${open ? 'pi-chevron-up' : 'pi-chevron-down'} text-xs text-400`} />
                </div>
            </div>

            {/* Body */}
            {open && (
                <div className="p-3">
                    {isAnalyzing ? (
                        <div className="flex align-items-center gap-3 p-3 border-round" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                            <i className="pi pi-spin pi-spinner" style={{ color: '#D97706' }} />
                            <span className="text-sm" style={{ color: '#92400E' }}>Documento en proceso de análisis. Los datos aparecerán al completarse.</span>
                        </div>
                    ) : isFailed ? (
                        <div className="flex align-items-center gap-3 p-3 border-round" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
                            <i className="pi pi-exclamation-triangle" style={{ color: '#DC2626' }} />
                            <span className="text-sm" style={{ color: '#991B1B' }}>El análisis de este documento falló. Puede reintentarse desde la pestaña Documentos.</span>
                        </div>
                    ) : isDesconocido && !doc.extracted ? (
                        <div className="flex align-items-center gap-3 p-3 border-round" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                            <i className="pi pi-question-circle" style={{ color: '#D97706' }} />
                            <span className="text-sm" style={{ color: '#92400E' }}>Este documento no pudo clasificarse como un tipo legal conocido. Verifique el documento.</span>
                        </div>
                    ) : !doc.extracted ? (
                        <div className="text-sm text-500 font-italic">Sin datos extraídos.</div>
                    ) : (
                        <>
                            {doc.detected_type === 'ACTA_CONSTI' && <ActaFields ext={ext} />}
                            {doc.detected_type === 'PODERES' && <PoderesFields ext={ext} />}
                            {doc.detected_type === 'REGISTRO_PUBLICO' && <RegistroFields ext={ext} />}
                            {isDesconocido && doc.extracted && (
                                <div className="text-sm text-500 font-italic mb-2">
                                    Tipo no reconocido — mostrando campos disponibles:
                                </div>
                            )}
                            <ExtraFields ext={ext} type={doc.detected_type} />
                        </>
                    )}
                    {editContent}
                </div>
            )}
        </div>
    );
};

// ─── Panel principal ──────────────────────────────────────────────────────────
const LegalAnalysisPanel = ({ persona, toast, dirty, onDirtyReset }) => {
    const [legalData, setLegalData] = useState(null);
    const [loading, setLoading] = useState(false);
    const pollRef = useRef(null);

    // ─── Override edit state ──────────────────────────────────────────────────
    const [modalDoc, setModalDoc] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [activeOverridePaths, setActiveOverridePaths] = useState([]);
    const [saveReason, setSaveReason] = useState('');
    const [clearPathsQueue, setClearPathsQueue] = useState([]);
    const [savingOverride, setSavingOverride] = useState(false);
    const [loadingOverrides, setLoadingOverrides] = useState(false);

    const fetchLegal = useCallback(async (silent = false) => {
        if (!persona?.id) return;
        if (!silent) setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${url}/document-analysis/legal/${persona.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLegalData(res.data);
        } catch (err) {
            if (err.response?.status === 404) {
                setLegalData({ documents: [] });
            } else {
                toast?.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron obtener los datos legales.', life: 3000 });
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, [persona?.id]);

    const openEditForm = useCallback(async (doc) => {
        // Open modal immediately with loading state
        setModalDoc(doc);
        setLoadingOverrides(true);
        setClearPathsQueue([]);
        setSaveReason('');

        const resolved = normalizeExtracted(doc.resolved_extracted || doc.extracted || {});
        const fields = getLegalFields(doc.detected_type);
        const initialValues = {};
        fields.forEach((field) => {
            const v = resolved[field.path];
            if (v !== null && v !== undefined) {
                initialValues[field.path] = deserializeFieldValue(field, v);
            }
        });
        setEditValues(initialValues);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${url}/document-analysis/items/${doc.id}/operational-overrides`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const overrides = res.data.overrides || [];
            setActiveOverridePaths(overrides.map(o => o.field_path));
            // Apply confirmed overrides on top of initial values
            overrides.forEach(o => {
                const field = fields.find(f => f.path === o.field_path);
                if (field) {
                    initialValues[o.field_path] = deserializeFieldValue(field, o.confirmed_value);
                } else {
                    initialValues[o.field_path] = o.confirmed_value;
                }
            });
            setEditValues({ ...initialValues });
        } catch {
            setActiveOverridePaths([]);
        } finally {
            setLoadingOverrides(false);
        }
    }, []);

    const handleClearPath = useCallback((path) => {
        setClearPathsQueue(q => q.includes(path) ? q : [...q, path]);
        setEditValues(v => { const n = { ...v }; delete n[path]; return n; });
        setActiveOverridePaths(p => p.filter(x => x !== path));
    }, []);

    const saveLegalOverrides = useCallback(async (doc) => {
        setSavingOverride(true);
        try {
            const token = localStorage.getItem('token');
            const fields = getLegalFields(doc.detected_type);
            const values = {};

            fields.forEach((field) => {
                const v = editValues[field.path];
                if (v !== undefined && v !== '' && v !== null) {
                    values[field.path] = serializeFieldValue(field, v);
                }
            });

            // Use effective_override_category from backend (handles mismatch correctly)
            const category = doc.effective_override_category || categoryForType(doc.detected_type);

            await axios.post(
                `${url}/document-analysis/items/${doc.id}/operational-overrides`,
                { category, save_reason: saveReason || 'Corrección manual desde panel legal', values, clear_paths: clearPathsQueue },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast?.current?.show({ severity: 'success', summary: 'Guardado', detail: 'Datos legales confirmados guardados.', life: 3000 });
            setModalDoc(null);
            fetchLegal(true);
        } catch (err) {
            toast?.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'No se pudieron guardar los datos.', life: 4000 });
        } finally {
            setSavingOverride(false);
        }
    }, [editValues, saveReason, clearPathsQueue, fetchLegal]);

    useEffect(() => { fetchLegal(); }, [fetchLegal]);

    // Refresh cuando llega dirty flag desde MesaControlPanel
    useEffect(() => {
        if (dirty) {
            fetchLegal();
            onDirtyReset?.();
        }
    }, [dirty, fetchLegal, onDirtyReset]);

    // Polling automático si hay documentos en proceso
    const hasProcessing = legalData?.documents?.some(d => d.status === 'processing' || d.status === 'pending');
    useEffect(() => {
        if (hasProcessing) {
            pollRef.current = setTimeout(() => fetchLegal(true), 10000);
        }
        return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    }, [legalData, hasProcessing, fetchLegal]);

    // Datos derivados
    const docs = (legalData?.documents || []).filter(d => d.review_status !== 'rejected');
    const validDocs = docs.filter(isValidLegalDoc);
    const completedDocs = validDocs.filter(d => isDocCompleted(d.status));
    const { vigentes, empatadas, maxDate } = computeVigencia(docs);
    const vigentIds = new Set(vigentes.map(d => d.id));
    const { signatories, alerts: signatoryAlerts } = getSignatories(vigentes, completedDocs, maxDate);
    const cuadroSources = getCuadroData(vigentes, completedDocs);
    const sortedDocs = [...docs].sort((a, b) => {
        if (a.vigencia_date && b.vigencia_date) return b.vigencia_date.localeCompare(a.vigencia_date);
        if (a.vigencia_date) return -1;
        if (b.vigencia_date) return 1;
        return 0;
    });

    if (loading && !legalData) return <LegalSkeleton />;

    if (!legalData || docs.length === 0) {
        return (
            <div className="flex flex-column align-items-center justify-content-center p-4 gap-3" style={{ minHeight: '10rem' }}>
                <i className="pi pi-folder-open text-3xl text-400" />
                <span className="text-sm text-500">No hay documentos legales vigentes para esta persona.</span>
                <AppButton label="Actualizar" icon="pi pi-refresh" variant="secondary" size="sm" onClick={() => fetchLegal()} loading={loading} />
            </div>
        );
    }

    return (
        <>
            <style>{`@keyframes legalSkel{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
            <div className="flex flex-column gap-3">

                {/* Header */}
                <div className="flex align-items-center justify-content-between">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-file-edit text-lg" style={{ color: PURPLE }} />
                        <span className="text-sm font-bold" style={{ color: PURPLE, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Análisis Legal Corporativo
                        </span>
                    </div>
                    <AppIconButton icon="pi pi-refresh" round onClick={() => fetchLegal()} loading={loading} title="Actualizar" />
                </div>

                {/* Bento */}
                <div className="flex gap-3 flex-wrap">
                    <VigenciaCard vigentes={vigentes} empatadas={empatadas} allDocs={docs} maxDate={maxDate} />
                    <SignatoriesCard signatories={signatories} alerts={signatoryAlerts} />
                    {cuadroSources.length > 0 && <CuadroAccionarioCard cuadroSources={cuadroSources} />}
                </div>

                {/* Expedientes */}
                <div>
                    <div className="flex align-items-center gap-2 mb-3 pb-2 border-bottom-1 surface-border">
                        <i className="pi pi-briefcase text-base" style={{ color: PURPLE }} />
                        <span className="text-sm font-bold text-700" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Expedientes Corporativos
                        </span>
                        <span className="ml-auto text-xs text-400">
                            {sortedDocs.length} {sortedDocs.length === 1 ? 'documento' : 'documentos'}
                        </span>
                    </div>

                    <div className="flex flex-column gap-3">
                        {sortedDocs.map(doc => {
                            const hasEditableFields = !!LEGAL_SCHEMA[doc.detected_type];
                            return (
                                <DocCard
                                    key={doc.id}
                                    doc={doc}
                                    isVigente={vigentIds.has(doc.id)}
                                    isEmpatado={empatadas && vigentIds.has(doc.id)}
                                    onEditClick={hasEditableFields ? () => openEditForm(doc) : undefined}
                                />
                            );
                        })}
                    </div>

                    {empatadas && (
                        <div className="mt-2 text-xs text-400 font-italic">
                            * Los documentos marcados con asterisco comparten la misma fecha de vigencia.
                        </div>
                    )}
                </div>

                {/* ── Modal de edición ── */}
                <OperationalReviewModal
                    isOpen={!!modalDoc}
                    onClose={() => setModalDoc(null)}
                    title="Editar Datos Legales"
                    subtitle={modalDoc ? `${modalDoc.file_name || 'Documento'} — ${getTypeMeta(modalDoc.detected_type).label}` : ''}
                    accentColor={modalDoc ? getTypeMeta(modalDoc.detected_type).color : PURPLE}
                    documentUrl={modalDoc?.signed_url}
                    documentName={modalDoc?.file_name}
                    saving={savingOverride}
                    loading={loadingOverrides}
                    metaContent={modalDoc ? (
                        <div className="flex flex-wrap gap-2 align-items-center">
                            <Tag value={getTypeMeta(modalDoc.detected_type).label}
                                style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', background: `${getTypeMeta(modalDoc.detected_type).color}20`, color: getTypeMeta(modalDoc.detected_type).color }} rounded />
                            {modalDoc.vigencia_date && (
                                <span className="text-xs text-500">Vigencia: <strong>{modalDoc.vigencia_date}</strong></span>
                            )}
                            {modalDoc.has_type_mismatch && (
                                <Tag value="Tipo reclasificado" severity="info" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />
                            )}
                            {modalDoc.has_manual_overrides && (
                                <Tag value={`Corregido (${modalDoc.manual_override_count})`} rounded
                                    style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', background: `${getTypeMeta(modalDoc.detected_type).color}20`, color: getTypeMeta(modalDoc.detected_type).color }} />
                            )}
                            {modalDoc.operational_completeness?.missing_required_paths?.length > 0 && (
                                <Tag value={`Faltan ${modalDoc.operational_completeness.missing_required_paths.length}`} severity="warning" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />
                            )}
                        </div>
                    ) : null}
                >
                    {modalDoc && LEGAL_SCHEMA[modalDoc.detected_type] && (
                        <LegalEditForm
                            doc={modalDoc}
                            activeOverridePaths={activeOverridePaths}
                            editValues={editValues}
                            saveReason={saveReason}
                            onFieldChange={(path, val) => setEditValues(v => ({ ...v, [path]: val }))}
                            onReasonChange={setSaveReason}
                            onClearPath={handleClearPath}
                            onSave={() => saveLegalOverrides(modalDoc)}
                            onCancel={() => setModalDoc(null)}
                            saving={savingOverride}
                        />
                    )}
                </OperationalReviewModal>

            </div>
        </>
    );
};

export default LegalAnalysisPanel;
